<template>
  <div class="page-content">
    <div class="headings">
      <h2 class="title title-primary">{{ location }}</h2>
      <span>|</span>
      <h2 class="title title-primary">
        {{ $t("lang.inbound.order_detail.page_title_line_1") }}
      </h2>
      <span>|</span>
      <h3 class="title title-primary">
        {{ $t("lang.inbound.order_detail.page_title_line_2") }}
      </h3>
    </div>
    <div class="page-header">
      <div class="boxes" v-if="!shouldLoading">
        <bx-box
          dark
          color="outline"
          title="Orden"
          class="col-12 col-md-6 col-lg-auto p-1"
        >
          <template slot="actions">
            <bx-order-status :status="status" dark></bx-order-status>
          </template>
          <div class="detail-titles">
            <div class="wrapper">
              <h4>
                Nº
                <strong>{{ id }}</strong>
              </h4>
              <h5>
                <strong>{{ name }}</strong>
              </h5>
            </div>
          </div>
        </bx-box>

        <bx-box dark color="outline" class="col-6 col-md-6 col-lg-auto p-1">
          <bx-value
            :label="$t('lang.inbound.order_detail.box_label_2')"
            :value="receptionTime"
            font-size="xs"
          />
        </bx-box>

        <bx-box
          dark
          color="outline"
          class="col-6 col-md-4 col-lg-auto p-1 bx-box-adjustable"
        >
          <bx-value
            :label="$t('lang.inbound.order_detail.box_label_3')"
            :value="waitingTime"
            :valueColor="valueColor"
            font-size="xs"
          />
        </bx-box>

        <bx-box dark color="outline" class="col-6 col-md-4 col-lg-auto p-1">
          <bx-value
            :label="$t('lang.inbound.order_detail.box_label_4')"
            :value="`${this.real_item_count} / ${this.item_count}`"
            font-size="xs"
          />
        </bx-box>

        <bx-box dark color="outline" class="col-6 col-md-4 col-lg-auto p-1">
          <bx-value
            :label="$t('lang.inbound.order_detail.box_label_5')"
            :value="`${this.num_verified_boxes} / ${this.num_boxes}`"
            font-size="xs"
          />
        </bx-box>
      </div>
      <skeleton-boxes :boxes="5" v-else />
    </div>

    <div class="page-main-content">
      <div class="container-fluid">
        <div class="row" style="margin: 8px">
          <bx-btn @click="$router.back()">{{
            $t("lang.messages.go_back")
          }}</bx-btn>
        </div>
        <v-row class="justify-content-end">
          <PrintWithItems
            :is-disabled="selectedRecords.length < 1"
            @print="print"
            :records="selectedRecords"
            :order_num="Number(id)"
          ></PrintWithItems>
        </v-row>
        <div class="row">
          <div class="col-12">
            <bx-box
              :title="
                itemCount + ' ' + $t('lang.inbound.order_detail.table_title')
              "
              color="white"
              no-padding
              :loading="shouldLoading"
            >
              <template v-slot:default>
                <bx-table
                  :data="data"
                  :columns="columns"
                  paginated
                  v-if="!shouldLoading"
                  backendPagination
                  :currentPage="page"
                  :perPage="itemsPerPage"
                  :total="itemCount"
                  @update:currentPage="updatePage"
                >
                  <template slot-scope="props">
                    <bx-table-column>
                      <router-link
                        :to="
                          `/inbound/order/${id}/gtin/${props.row.itemType}/detail`
                        "
                        class="bx-link bx-link-bold"
                        >{{ props.row.gtin }}</router-link
                      >
                    </bx-table-column>
                    <bx-table-column>{{ props.row.quantity }}</bx-table-column>
                    <bx-table-column>{{props.row.quantity_real}}</bx-table-column>
                    <bx-table-column>{{props.row.description}}</bx-table-column>
                    <bx-table-column>{{ props.row.section }}</bx-table-column>
                    <bx-table-column>{{props.row.cloth_type}}</bx-table-column>
                    <bx-table-column>{{ props.row.model }}</bx-table-column>
                    <bx-table-column>{{ props.row.color }}</bx-table-column>
                    <bx-table-column>{{ props.row.size }}</bx-table-column>
                    <bx-table-column v-if="props.row.ratingPercent > 100">
                      <bx-complementary-mini
                        :percent="100"
                      ></bx-complementary-mini>
                    </bx-table-column>
                    <bx-table-column v-if="props.row.ratingPercent <= 100">
                      <bx-complementary-mini
                        :percent="props.row.ratingPercent"
                      ></bx-complementary-mini>
                    </bx-table-column>
                    <bx-table-column
                      centered
                      class="align-items-start d-flex justify-content-center"
                    >
                      <v-checkbox
                        class="shrink mr-0 mt-0 pt-0"
                        color="#9ac31c"
                        hide-details
                        @change="onSelectRecord(props.row)"
                        :value="props.row.id"
                        v-model="selectedRows"
                      ></v-checkbox>
                    </bx-table-column>
                  </template>
                </bx-table>
              </template>
            </bx-box>
          </div>
        </div>
        <v-row class="d-flex d-sm-none">
          <div
            v-infinite-scroll="loadMore"
            infinite-scroll-disabled="busy"
            infinite-scroll-distance="20"
            style="width: 100%"
          >
            <v-list width="100%">
              <v-list-item
                two-line
                v-for="(item, index) in dataList"
                :key="index"
                class="green lighten-5 ma-2 rounded-lg"
                ripple
                :to="`/inbound/order/${id}/gtin/${item.itemType}/detail`"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <div>
                      {{ item.gtin }}
                    </div>
                  </v-list-item-title>
                  <v-list-item-subtitle>{{
                    item.section
                  }}</v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption">{{
                    item.cloth_type
                  }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-list-item-subtitle class="text-caption">
                    {{ item.quantity_real + "/" + item.quantity }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption">
                    {{ item.color }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption">
                    {{ item.model }}
                  </v-list-item-subtitle>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </v-row>
        <skeleton-table :columns="9" v-if="shouldLoading" />
      </div>
    </div>
  </div>
</template>

<script>
import PrintWithItems from "@/components/PrintWithItems";
import BxTable from "../../../bx-components/BxTable";
import BxBtn from "../../../bx-components/BxBtn";
import BxTableColumn from "../../../bx-components/BxTableColumn";
import BxOrderStatus from "../../../bx-components/BxOrderStatus";
import moment from "moment";
import common from "@/utils/common";

export default {
  name: "inbound-order-detail",
  mixins: [common],
  components: { BxOrderStatus, BxTableColumn, BxTable, BxBtn, PrintWithItems },
  props: {
    id: {
      type: [Number, String]
    }
  },
  data() {
    return {
      shouldLoading: true,
      columns: [],
      data: [],
      dataList: [],
      selectedRows: [],
      selectedRecords: [],
      description: "",
      orderData: null,
      receptionTime: "",
      waitingTime: "",
      itemCount: "",
      name: "",
      num_verified_boxes:0,
      num_boxes:0,
      status: 0,
      contents: [],
      reloadInterval: null,
      API_BASE: process.env.VUE_APP_API_BASE,
      itemsPerPage: process.env.VUE_APP_GTIN_PER_PAGE,
      page: 1,
      valueColor: "white"
    };
  },
  computed: {
    location() {
      return localStorage.getItem("location");
    },
  },
  methods: {
    loadMore() {
      if (
        !this.shouldLoading &&
        this.dataList.length &&
        this.dataList.length < this.itemCount &&
        this.isMobile
      ) {
        this.page++;
        this.getData();
      }
    },
    async print(printer) {
      this.selectedRows.forEach(async (value, id) => {
        await this.$api.porders.print(value, printer);
      });
    },
    onSelectRecord(item) {
      let foundIndex = this.selectedRecords.findIndex(x => x.id === item.id);
      if (foundIndex === -1) {
        this.selectedRecords.push(item);
      } else {
        this.selectedRecords.splice(foundIndex, 1);
      }
    },
    updatePage(p) {
      this.page = p;
      this.getData(this.locationId);
    },
    async getData() {
      if (this.isMobile) {
        this.shouldLoading = true;
      }

      let orderData = (this.orderData = await this.$api.porders.get(
        this.id
      ))[0];
      this.receptionTime = orderData.arrival_date;
      this.status = orderData.destination_status;
      this.waitingTime = orderData.waiting_time;
      this.name = orderData.order_name;
      this.num_boxes = orderData.num_boxes;
      this.num_verified_boxes = orderData.num_verified_boxes;
      this.real_item_count = orderData.real_item_count;
      this.item_count = orderData.item_count;



      let isAfter =
        orderData.length &&
        moment().isAfter(orderData[0].estimated_arrival_date);
      if (isAfter) {
        this.valueColor = "red";
      }

      this.report = await this.$api.porders.report(this.id, {
        offset: (this.page - 1) * this.itemsPerPage,
        limit: this.itemsPerPage
      });

      this.itemCount = this.report.count;
      this.data = this.report.results.map((item, key) => {
        return {
          gtin: item.gtin,
          itemType: item.item_type,
          quantity: item.theorical_quantity,
          quantity_real: item.real_quantity !== null ? item.real_quantity : 0,
          cloth_type: item.cloth_type,
          id: item.id,
          size: item.size,
          color: item.colour,
          model: item.model,
          section: item.section,
          description: item.description,
          ratingPercent: (item.real_quantity / item.theorical_quantity) * 100
        };
      });
      if (this.isMobile) {
        this.dataList = this.dataList.concat(this.data);
      }

      this.columns = [
        {
          field: "gtin",
          label: this.$t("lang.inbound.order_detail.table_field_1")
        },
        {
          field: "quantity",
          label: this.$t("lang.inbound.order_detail.table_field_2")
        },
        {
          field: "real_quantity",
          label: this.$t("lang.inbound.order_detail.table_field_3")
        },
        {
          field: "description",
          label: this.$t("lang.inbound.order_detail.description")
        },
        {
          field: "section",
          label: this.$t("lang.inbound.order_detail.table_field_4")
        },
        {
          field: "itemtype",
          label: this.$t("lang.inbound.order_detail.table_field_5")
        },
        {
          field: "model",
          label: this.$t("lang.inbound.order_detail.table_field_6")
        },
        {
          field: "color",
          label: this.$t("lang.inbound.order_detail.table_field_7")
        },
        {
          field: "size",
          label: this.$t("lang.inbound.order_detail.table_field_8")
        },
        {
          field: "ratingPercent",
          label: this.$t("lang.inbound.order_detail.table_field_9"),
          width: 200
        },
        {
          field: "action",
          label: this.$t("lang.inbound.order_detail.table_field_10")
        }
      ];

      this.shouldLoading = false;
    }
  },
  mounted() {
    if (!this.isMobile) {
      this.reloadInterval = setInterval(
        this.getData,
        process.env.VUE_APP_RELOAD_TIME
      );
    }
    this.getData();
  },
  beforeDestroy() {
    clearInterval(this.reloadInterval);
  }
};
</script>

<style scoped>
@media screen and (max-width: 480px) {
  .bx-box-adjustable >>> .bx-box-content {
    min-height: 95px;
  }

  .bx-box-adjustable >>> .bx-value-value {
    margin-top: 10px;
  }
}

@media screen and (max-width: 411px) {
  .bx-box-adjustable >>> .bx-box-content {
    min-height: 79px;
  }

  .bx-box-adjustable >>> .bx-value-value {
    margin-top: 0px;
  }
}

@media screen and (max-width: 375px) {
  .bx-box-adjustable >>> .bx-box-content {
    min-height: 91px;
  }

  .bx-box-adjustable >>> .bx-value-value {
    margin-top: 10px;
  }
}
</style>
